import React from 'react';
import { message, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CloseCircle, CloseSquare } from 'iconsax-react';
import { useRejectUserOperationMutation } from '../../store/services/business-requests';
import Button from '../atoms/Button';
import FormField from '../FormField/FormField';
import TitleText from '../atoms/TitleText';
import InfoText from '../atoms/InfoText';

const REASON_MIN = 20;
const REASON_MAX = 500;
/** English, Arabic, digits, special characters */
const REASON_PATTERN = /^[\p{L}\p{N}\p{P}\p{S}\s]*$/u;

const rejectReasonSchema = (t: (key: string) => string) =>
  z.object({
    reason: z
      .string()
      .trim()
      .min(REASON_MIN, t('home.reasonValidationMin'))
      .max(REASON_MAX, t('home.reasonValidation'))
      .regex(REASON_PATTERN, t('home.reasonValidation')),
  });

type RejectFormValues = z.infer<ReturnType<typeof rejectReasonSchema>>;

interface RejectModalProps {
  isModalOpen: boolean;
  handleModalClose: (value: boolean) => void;
  companyEmail: string;
  requestId: string;
}

const RejectModal = ({
  isModalOpen,
  handleModalClose,
  companyEmail,
  requestId,
}: RejectModalProps) => {
  const { t } = useTranslation();
  const [rejectRequest] = useRejectUserOperationMutation();

  const methods = useForm<RejectFormValues>({
    resolver: zodResolver(rejectReasonSchema(t)),
    defaultValues: { reason: '' },
    mode: 'onTouched',
  });

  const onSubmit = async (data: RejectFormValues) => {
    try {
      await rejectRequest({
        body: {
          rejectReason: data.reason.trim(),
          email: companyEmail,
        },
        id: requestId,
      }).unwrap();
      methods.reset();
      handleModalClose(true);
    } catch (error) {
      message.error('Something went wrong');
    }
  };

  const handleClose = () => {
    methods.reset();
    handleModalClose(false);
  };

  return (
    <Modal
      open={isModalOpen}
      onCancel={handleClose}
      closable={false}
      footer={null}
      centered
      destroyOnHidden
      width={600}
      data-test="reject-modal"
    >
      <div className="flex flex-col items-center justify-center gap-4 py-2" data-test="reject-modal-content">
        {/* Icon */}
        <div className="bg-red-50 w-12 h-12 rounded-xl flex justify-center items-center" data-test="reject-modal-icon">
          <CloseSquare size={24} color="#DC2626" />
        </div>

        {/* Title and Description */}
        <div className="flex flex-col gap-1 items-center text-center">
          <TitleText
            text={t('home.rejectRequest')}
            className="!font-semibold"
          />
          <InfoText
            text={t('home.rejectRequestDescription')}
            maxLength="FULL"
          />
        </div>

        {/* Reason Textarea */}
        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            className="w-full mt-2"
            data-test="reject-modal-reason-field"
          >
            <FormField
              id="reject-modal-reason"
              name="reason"
              type="textarea"
              label={t('home.reason')}
              placeholder={t('home.placeholder')}
              maxLength={REASON_MAX}
              helperText={methods.formState.errors.reason ? t('home.reasonHint') : undefined}
              showMaxLength
              data-test="reject-modal-reason-textarea"
            />

            {/* Buttons */}
            <div className="flex justify-center w-full gap-3 mt-2" data-test="reject-modal-actions">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                data-test="reject-modal-cancel-btn"
              >
                {t('home.cancel')}
              </Button>
              <Button
                type="submit"
                variant="danger"
                icon={<CloseCircle size={18} color="#ffffff" />}
                className="w-40"
                data-test="reject-modal-submit-btn"
              >
                {t('home.reject')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </Modal>
  );
};

export default RejectModal;
